import { ReactNode } from 'react'
import { Group, iconSizes, Stack, Text, Tooltip } from '@common/ui'
import { IconInfoCircle } from '@common/ui/icons'
import { com } from '@eidu/web'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import { anchorTypeIdOf, isAllRowPermission, isGlobalRowPermission } from './utils'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntitySelector = com.eidu.entity.selector.EntitySelector
import EntityAction = com.eidu.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.entity.permission.EntityPathPermission
import Action = com.eidu.entity.permission.Action
import Permission = com.eidu.entity.permission.Permission

export type PolicyMatch = { policyName: string; permission: Permission }

export const policyMatchesGlobalRow = (policies: ReadonlyArray<PermissionPolicyDTO>, action: Action): PolicyMatch[] =>
  policies.flatMap((p) =>
    [...p.policy.permissions.asJsReadonlySetView()]
      .filter((perm) => isGlobalRowPermission(perm, action))
      .map((permission) => ({ policyName: p.policy.name, permission }))
  )

export const policyMatchesEntityCell = (
  policies: ReadonlyArray<PermissionPolicyDTO>,
  entityTypes: readonly EntityType[],
  rowEntityTypeId: EntityTypeId,
  action: EntityAction
): PolicyMatch[] =>
  policies.flatMap((p) =>
    [...p.policy.permissions.asJsReadonlySetView()]
      .filter((perm) => {
        if (perm.action !== action) return false
        if (isAllRowPermission(perm, action)) return true
        const anchor = anchorTypeIdOf(perm, entityTypes)
        return anchor !== null && anchor.equals(rowEntityTypeId)
      })
      .map((permission) => ({ policyName: p.policy.name, permission }))
  )

const PolicyInfoBadge = ({ matches }: { matches: PolicyMatch[] }) => {
  if (matches.length === 0) return null
  const names = Array.from(new Set(matches.map((m) => m.policyName))).sort((a, b) => a.localeCompare(b))
  return (
    <Tooltip label={`Granted by polic${names.length > 1 ? 'ies' : 'y'}: ${names.join(', ')}`}>
      <span
        aria-label={`Granted by polic${names.length > 1 ? 'ies' : 'y'}: ${names.join(', ')}`}
        style={{
          position: 'absolute',
          top: 2,
          right: 2,
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: 'var(--trellis-color-accent)',
          pointerEvents: 'auto',
        }}
      />
    </Tooltip>
  )
}

export const CheckboxWithPolicyBadge = ({ matches, children }: { matches: PolicyMatch[]; children: ReactNode }) => (
  <span style={{ position: 'relative', display: 'inline-flex' }}>
    {children}
    <PolicyInfoBadge matches={matches} />
  </span>
)

const describePolicyPermission = (permission: Permission): string => {
  if (permission instanceof EntityPathPermission) return `Assignment → ${permission.path.toType.toString()}`
  if (permission instanceof EntitySelectorPermission) {
    if (EntitySelector.All.equals(permission.targets)) return 'All entities'
    if (permission.targets instanceof EntitySelector.OfType) return 'All of type'
    if (permission.targets instanceof EntitySelector.Single) return 'Specific entity'
    return permission.targets.toString()
  }
  return 'Global'
}

export const PolicyMatchesReadOnlyBlock = ({ matches }: { matches: PolicyMatch[] }) => {
  if (matches.length === 0) return null
  return (
    <div
      style={{
        border: '1px dashed var(--trellis-color-primary)',
        borderRadius: 'var(--trellis-radius-xs)',
        padding: 'var(--trellis-spacing-xs)',
        backgroundColor: 'var(--trellis-color-surface)',
      }}
    >
      <Group gap="xxs" align="center" mb="xxs" wrap="nowrap">
        <IconInfoCircle size={iconSizes.xs} stroke={1.5} color="var(--trellis-color-primary)" />
        <Text size="xs" c="dimmed">
          Granted by {matches.length === 1 ? 'policy' : 'policies'} (read-only)
        </Text>
      </Group>
      <Stack gap="xxs">
        {matches.map((m, i) => (
          <Text key={i} size="sm" c="dimmed">
            <span style={{ fontWeight: 600 }}>{m.policyName}</span>
            {' — '}
            {describePolicyPermission(m.permission)}
          </Text>
        ))}
      </Stack>
    </div>
  )
}
